import { useCallback, useContext, useRef, useState } from 'react'
import * as React from 'react'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import TextInput from '../Form/Input/TextInput'
import FoldableContent from '../FoldableContent/FoldableContent'
import styled from 'styled-components'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ContactContext from 'contexts/contactContext'
import { redeemGiftCard } from 'api/promo'
import { validateAndFetchGiftCardBalance } from 'api/payment'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import AnnotatedGiftCard from './AnnotatedGiftCard'
import type { RedeemedCard } from './common'
import DividerWithText from '../DividerWithText'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import useToggle from 'hooks/useToggle'
import ScanCode from './ScanCode'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useStateWithRef from 'hooks/useStateWithRef'
import { rem } from 'polished'
import { trackClientEvent } from 'analytics/analytics'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'
import { isBlackhwakCard } from 'components/Common/RedeemGiftCard/utils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setUsingGiftCard } from 'actions/CheckoutActions'
import HelpSection from 'checkout/Components/Shared/CustomerServiceInformation/HelpSection'
import config from 'constants/config'
import { useIntl } from 'react-intl'

type RedeemToGiftCardResult = {
  cardNumber?: string
  amount: number
  currency: string
  expiresAt?: string
}

type RedeemToCreditsResult = {
  currency: string
  amount: string
}

const StyledGiftGardImage = styled(AnnotatedGiftCard)`
  margin: 0 auto;
  width: 100%;
`

const ScanGiftCardButton = styled(ClickableListItem)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const ErrorExampleList = styled.ul`
  margin-top: ${rem(12)};
  margin-bottom: 0;
`

const StyledHelpSection = {
  textAlign: 'center' as const,
  display: 'flex' as const,
  flexDirection: 'column' as const,
  alignItems: 'center' as const,
  padding: '16px',
}

const ErrorBanner = styled(MessageBanner)`
  margin-top: ${rem(24)};
`

interface Props {
  onRedeem: (redeemedCard: RedeemedCard) => void
  onBack?: () => void
  enableScan?: boolean
}

function EnterCode({ onRedeem, onBack: moveToSummary, enableScan }: Props) {
  const [code, setCode, codeRef] = useStateWithRef('')
  const [pin, setPin, pinRef] = useStateWithRef('')
  const [processing, setProcessing] = useState(false)
  const [hasError, setHasError] = useState(false)
  const intl = useIntl()
  const [errorMessage, setErrorMessage] = useState(intl.formatMessage({
    id: 'npw9CO48FpAl_pLBj8Dp',
    defaultMessage: 'Please check and try again.',
    description: 'Default error message for the gift card redemption',
  }))
  const { value: isScanMode, on: selectScanMode, off: deselectScanMode } = useToggle()
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [didScanCard, setDidScanCard] = useState(false)
  const didEditManually = useRef(false)

  const { currentRegionCode } = useContext(GeoContext)
  const { defaultContact } = useContext(ContactContext)
  const analyticsPage = useContext(AnalyticsPageContext)

  const shouldAskPin = isBlackhwakCard(code)
  const showSupportNumber = failedAttempts > 2

  const dispatch = useAppDispatch()

  const isGiftCardV2 = config.ENABLE_GIFT_CARD_V2
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)

  const onCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
    didEditManually.current = true
  }, [setCode])

  const onPinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value)
    didEditManually.current = true
  }, [setPin])

  const onSubmit = useCallback(async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setProcessing(true)
    setHasError(false)

    try {
      let redeemResult: RedeemToGiftCardResult | RedeemToCreditsResult
      const sanitisedCode = codeRef.current.toUpperCase().replace(/[^0-9A-Z]/g, '')
      if (isGiftCardV2 && !isSpoofed) {
        const redeemResponse = await validateAndFetchGiftCardBalance(sanitisedCode, currentRegionCode, pinRef.current)
        redeemResult = redeemResponse.result
      } else {
        const redeemResponse = await redeemGiftCard(codeRef.current, pinRef.current, currentRegionCode)
        redeemResult = redeemResponse.result.credits
      }

      if (didScanCard) {
        if (didEditManually.current) {
          trackClientEvent({
            subject: 'scan_gift_card',
            action: 'success_modified_from_scan',
            category: analyticsPage,
            type: 'nonInteraction',
          })
        } else {
          trackClientEvent({
            subject: 'scan_gift_card',
            action: 'success_exact_match',
            category: analyticsPage,
            type: 'nonInteraction',
          })
        }
      }

      dispatch(setUsingGiftCard(true))

      onRedeem({
        code: sanitisedCode,
        currencyCode: redeemResult.currency,
        amount: parseFloat(String(redeemResult.amount)),
        ...(pinRef.current ? { pin: pinRef.current } : {}),
      })

      if (!isGiftCardV2) {
        const displayCreditAmount = formatDynamicAmountWithDollarType(
          parseFloat(String(redeemResult.amount)),
          redeemResult.currency,
          currentRegionCode,
        )

        showSnackbar(
          intl.formatMessage({
            id: 'ptWwC1rd1BB2d_MS5VB6',
            defaultMessage: '{amount} credit has been applied to your account',
            description: 'Success notification displayed when gift card credit is applied',
          }, {
            amount: displayCreditAmount,
          }),
          'success',
          {
            heading: intl.formatMessage({
              id: 'Faa73eYc_-otGZ4f4GKO',
              defaultMessage: 'Gift card redeemed',
              description: 'Snackbar heading for the gift card redemption success',
            }),
          },
        )
      }
    } catch(e: any) {
      setHasError(true)
      if (e.status === 400) { // display api error message for 400 status
        setErrorMessage(e.message)
      } else if (e.message) { // handle validation errors from validateGiftCard
        setErrorMessage(e.message)
      }
      setFailedAttempts(n => n + 1)
    } finally {
      setProcessing(false)
    }
  }, [codeRef, pinRef, didScanCard, onRedeem, currentRegionCode, analyticsPage, dispatch, isGiftCardV2, isSpoofed, intl])

  const onSelectScanMode = useCallback(() => {
    trackClientEvent({
      subject: 'scan_gift_card',
      action: 'start',
      category: analyticsPage,
      type: 'interaction',
    })
    selectScanMode()
    setHasError(false)
  }, [selectScanMode, analyticsPage])

  const onScanSuccess = useCallback((code: string, pin: string) => {
    setCode(code)
    setPin(pin)
    setDidScanCard(true)
    didEditManually.current = false
    deselectScanMode()
  }, [deselectScanMode, setCode, setPin])

  if (isScanMode) {
    return <ScanCode onSuccess={onScanSuccess} onCancel={deselectScanMode} />
  }

  return (
    <form onSubmit={onSubmit}>
      <ModalHeader
        title={intl.formatMessage({
          id: 'kaUKSrdlAQKvHxzAixyG',
          defaultMessage: 'Add a Gift Card',
          description: 'Modal title for adding gift card on the gift card purchase or redemption page',
        })}
        onBackButtonClick={moveToSummary}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText variant="large" as="p">
              {intl.formatMessage({
                id: 'LSid27XwofTeXeI6yxgw',
                defaultMessage: 'Enter the code from your printed voucher, email, or the back of your physical gift card.',
                description: 'Instructions for entering gift card code on the gift card page',
              })}
            </BodyText>
            <TextInput
              label={intl.formatMessage({
                id: 'OB2-SyNGjfC0xoQqbpTs',
                defaultMessage: 'Gift card code',
                description: 'Label for gift card code input on the gift card purchase or redemption page',
              })}
              placeholder={intl.formatMessage({
                id: 'OhhQ-m1uB6ux5g6b9PNb',
                defaultMessage: 'Enter code or gift card number here',
                description: 'Placeholder for gift card code input on the gift card purchase or redemption page',
              })}
              value={code}
              onChange={onCodeChange}
              disabled={processing}
              manualError
              required
              data-testid="code-field"
            />

            {shouldAskPin &&
              <TextInput
                label={
                  <LabelGroup>
                    {intl.formatMessage({
                      id: 'NFB_t8e4CMgnmgcqgAi9',
                      defaultMessage: 'Access code',
                      description: 'Form field label for the access code input in the exclusive offer redemption form',
                    })}
                    <Tooltip description={intl.formatMessage({
                      id: '7YbHiVD7v7AfM7UAAf3z',
                      defaultMessage: 'Enter the access code that is located in the scratch panel on the back of your physical gift card.',
                      description: 'Tooltip description for access code input on the gift card page',
                    })}
                    >
                      <LineInfoCircleIcon size="XS" />
                    </Tooltip>
                  </LabelGroup>
                }
                placeholder={intl.formatMessage({
                  id: 'VOW-ZVSiBCYnDbTO7QnT',
                  defaultMessage: 'e.g. XRNJU13RJL',
                  description: 'Placeholder example for access code input when field is empty',
                })}
                value={pin}
                onChange={onPinChange}
                disabled={processing}
                required
                data-testid="pin-field"
              />}
          </VerticalSpacer>
          <>
            <FoldableContent
              openLabel={intl.formatMessage({
                id: 'YJEOH26NR7mdRoIeeag-',
                defaultMessage: 'Where are my card details?',
                description: 'Label for foldable content about card details location',
              })}
              closedLabel={intl.formatMessage({
                id: 'YJEOH26NR7mdRoIeeag-',
                defaultMessage: 'Where are my card details?',
                description: 'Label for foldable content about card details location',
              })}
              size="medium"
            >
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'kMjmRfkijmMUguSkvQv8',
                  defaultMessage: 'For physical cards, the card number is located on the back of your card.',
                  description: 'Help text about physical card number location in the gift card section',
                })}
              </BodyText>
              <br />

              <StyledGiftGardImage />

            </FoldableContent>

            {enableScan && (
              <VerticalSpacer gap={16}>
                <DividerWithText
                  text={intl.formatMessage({
                    id: 'Iyusyo2z0x6NwA0uaHIB',
                    defaultMessage: 'or',
                    description: 'Divider text between manual entry and scan option on the form',
                  })}
                />

                <ScanGiftCardButton
                  title={intl.formatMessage({
                    id: 'HBBjVPTaqJ7fvzMI8A6L',
                    defaultMessage: 'Scan gift card',
                    description: 'Button text to scan a physical gift card using the device camera for redemption',
                  })}
                  size="large"
                  startIcon={<LineCameraIcon />}
                  endIcon={<LineAngleRightBIcon />}
                  onClick={onSelectScanMode}
                />
              </VerticalSpacer>
            )}
          </>
          {hasError && (
            <VerticalSpacer gap={16}>
              <ErrorBanner
                kind="critical"
                heading={intl.formatMessage({
                  id: 'd-_JAi6EmshlkH1UwY_w',
                  defaultMessage: 'Unable to redeem',
                  description: 'Error banner heading for gift card redemption failure',
                })}
              >
                <VerticalSpacer gap={12}>
                  {!didScanCard && !showSupportNumber && errorMessage}
                  {didScanCard && <>
                    <div>
                      {intl.formatMessage({
                        id: 'IQ-I0evObNQObudYsa_h',
                        defaultMessage: 'Sometimes the card scanner makes mistakes, such as confusing these characters:',
                        description: 'Error notification displayed about scanner mistakes',
                      })}
                    </div>
                    <ErrorExampleList>
                      <li>
                        {intl.formatMessage({
                          id: 'IRS__0TuQqeEgGAKDVVo',
                          defaultMessage: '8 and B',
                          description: 'Example of confusing characters for gift card code entry validation',
                        })}
                      </li>
                      <li>
                        {intl.formatMessage({
                          id: 'pkGgr4J07DTzeVy_jdQ0',
                          defaultMessage: '1 and I',
                          description: 'Example of confusing characters shown during gift card code entry',
                        })}
                      </li>
                      <li>
                        {intl.formatMessage({
                          id: 'WvSn-CDWNKo-6vqPRNa5',
                          defaultMessage: 'Q, G and 6',
                          description: 'Example of confusing characters in the gift card section',
                        })}
                      </li>
                    </ErrorExampleList>
                    <div>
                      {intl.formatMessage({
                        id: 'BmT2QgtXsJj1f77oKxv3',
                        defaultMessage: 'Please check the details carefully and try again.',
                        description: 'Error message asking user to check details when an issue occurs',
                      })}
                    </div>
                    {showSupportNumber && <div>
                      {intl.formatMessage({
                        id: 'FT0m6cGOR2w0B0MVlqcN',
                        defaultMessage: 'If you’re still having trouble, call us on {contactNumber}.',
                        description: 'Error message with support contact number in the support area',
                      }, {
                        contactNumber: defaultContact.humanReadable,
                      })}
                    </div>}
                  </>}
                </VerticalSpacer>
              </ErrorBanner>
              <div style={StyledHelpSection}>
                <HelpSection contactNumber={defaultContact} />
              </div>
            </VerticalSpacer>
          )}

        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          type: 'submit',
          children: intl.formatMessage({
            id: 'X5OgEPOga6YEGzloho-L',
            defaultMessage: 'Redeem',
            description: 'Button text to redeem gift card on the gift card purchase or redemption page',
          }),
          disabled: processing,
        }}
      />
    </form>
  )
}

export default EnterCode
